import { FC, useCallback, useRef } from 'react';
import { useCombinedRefs } from '@dnd-kit/utilities';
import { css } from '@emotion/react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { PersonsV3 } from '@frontend/api-person';
import { IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Avatar, PopoverDialog, usePopoverDialog } from '@frontend/design-system';
import { PaginatedSearchableList, TaskFormRow } from '../../common';
import { TaskPatientTooltipContent } from '../../task-details/task-patient-tooltip/task-patient-tooltip-content';

interface PatientPickerProps {
  label: string;
  icon: IconName;
  buttonIconName: IconName;
  placeholder: string;
  onChange: (value: Person | null) => void;
  value: Person | null;
  isEditing?: boolean;
  isRemovable?: boolean;
  locationId?: string;
  disabled?: boolean;
  trackingIds?: {
    removeButton?: string;
    chipButton?: string;
  };
}

export const PatientPicker: FC<PatientPickerProps> = ({
  label,
  icon,
  buttonIconName,
  placeholder,
  onChange,
  value,
  isEditing,
  isRemovable,
  trackingIds,
  locationId,
  disabled = false,
}) => {
  const patientId = value?.personId;
  const name = value ? PersonsV3.PersonHelpers.getFullName(value) : '';

  const timeout = useRef<NodeJS.Timeout>();

  const {
    getDialogProps,
    getTriggerProps: getDropdownTriggerProps,
    close: closeDropdown,
    isOpen: isDropdownOpen,
  } = usePopoverDialog({
    placement: 'bottom-start',
  });

  // for patient profile tooltip
  const {
    getDialogProps: getTooltipDialogProps,
    getTriggerProps: getTooltipTriggerProps,
    close: closeTooltip,
    open: openTooltip,
    isOpen: isTooltipOpen,
  } = usePopoverDialog({
    placement: 'top',
  });

  const onSelect = useCallback(
    (person: Person) => {
      onChange(person);
      closeDropdown();
    },
    [closeDropdown, onChange]
  );

  const combinedRef = useCombinedRefs(getDropdownTriggerProps().ref, getTooltipTriggerProps().ref);

  const openPatientPopover = useCallback(() => {
    if (patientId) {
      openTooltip();
      clearTimeout(timeout.current);
    }
  }, [openTooltip, patientId]);

  const closePatientPopover = useCallback(() => {
    if (patientId) {
      timeout.current = setTimeout(() => closeTooltip(), 500);
    }
  }, [closeTooltip, patientId]);

  const getTriggerProps: typeof getDropdownTriggerProps = () => {
    return {
      ...getDropdownTriggerProps(),
      ref: combinedRef,
      onMouseEnter: openPatientPopover,
      onMouseLeave: closePatientPopover,
    };
  };

  const showTooltip = isEditing && patientId;
  const isTooltipDialogOpen = isDropdownOpen ? false : isTooltipOpen;
  const leftElement = name ? <Avatar name={name} size='xs' /> : null;

  return (
    <TaskFormRow
      label={label}
      icon={icon}
      placeholder={placeholder}
      buttonIconName={buttonIconName}
      value={name}
      getTriggerProps={getTriggerProps}
      isEditing={isEditing}
      isRemovable={isRemovable}
      trackingIds={trackingIds}
      onClickRemove={() => onChange(null)}
      leftElement={leftElement}
      disabled={disabled}
    >
      <PopoverDialog css={styles.dropdownStyle} {...getDialogProps()}>
        <PaginatedSearchableList onSelect={onSelect} selectedPatient={value} selectedLocationId={locationId} />
      </PopoverDialog>
      {showTooltip && (
        <PopoverDialog css={styles.tooltipStyle} {...getTooltipDialogProps()} isOpen={isTooltipDialogOpen}>
          <TaskPatientTooltipContent
            patientId={patientId}
            locationId={locationId}
            closeTooltip={closeTooltip}
            onMouseEnter={openPatientPopover}
            onMouseLeave={closePatientPopover}
          />
        </PopoverDialog>
      )}
    </TaskFormRow>
  );
};

const styles = {
  tooltipStyle: css`
    z-index: ${theme.zIndex.modals};
    padding: 0;
    min-width: 100px;
    min-height: 100px;
    align-items: center;
    display: flex;
    justify-content: center;
  `,
  dropdownStyle: css`
    width: 300px;
    height: 275px;
    overflow: hidden;
  `,
};
